import { Component, type ErrorInfo, type ReactNode } from 'react';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

const AUTH_STORAGE_KEYS = [
  'cas_jwt',
  'cas_user',
  'cas_redirect_path',
  'cas_redirect_count',
];

export default class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[AppErrorBoundary] Unhandled render error:', error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  private handleResetSession = () => {
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="container mx-auto flex min-h-screen max-w-2xl items-center justify-center p-6">
        <div className="w-full rounded-lg border bg-card p-6 shadow-sm">
          <h1 className="mb-2 text-2xl font-bold text-destructive">Something went wrong</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            The page hit an unexpected error. You can retry, or clear the local auth session and reload.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              onClick={this.handleRetry}
              type="button"
            >
              Reload page
            </button>
            <button
              className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
              onClick={this.handleResetSession}
              type="button"
            >
              Clear session and reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
